import { announceReprocessing, type ReprocessReceipt } from "../reprocessFeedback";
/**
 * 모델군·요금제 구간 API — /model-groups/*, /plan-buckets/*
 *
 * ⚠️ src/pages/Policy/policyApi.ts 를 도메인별로 분해한 조각이다(2026-08-30).
 *    파일 이름은 백엔드 server/src/v2/routes/policy/ 와 짝을 맞췄다.
 */
import { z } from "zod";
import { API, REPROCESS_TIMEOUT_MS, downloadSigned, fetchJson, fetchJsonForm } from "./shared";
import type { Carrier } from "./shared";
import type { FaceUploadGrid } from "./face";
import { withContract } from "../../../api/contract";
import type {
  PlanGroupGridConfig,
  PlanGroupGridMappings,
  PlanGroupGridTrial,
  PlanGroupMatrix,
  PlanGroupReview,
  PlanGroupReviewNote,
  PlanGroupUploadJob,
} from "../../../../shared/plan-group-grid";
import type { PlanSheetDraft, PlanSheetDraftCarrier } from "../../../../shared/plan-group-sheet";
import { stripBandWords } from "../planBucketLabels";

export { stripBandWords };

/** 통신사 표준 구간 1건 — 구두 검토의 구간 열 셀렉트 옵션 */
export type PlanBucket = {
  code: string; // "SK_109" — 합산이 읽는 표준 코드
  carrier: Carrier;
  channel?: PlanGroupChannel | null;
  label: string; // 표시용 ("109K 이상")
  minMonthly: number;
  maxMonthly: number | null;
  sortOrder?: number; // 같은 통신사 안 표시 순서
  description?: string; // 사람용 설명 — 왜 이 구간인가
};

/** 표준 구간 카탈로그 — carrier 미지정 시 전체 (업체 미지정 등록 건 대비) */
export function fetchPlanBuckets(carrier?: Carrier | null, channel?: PlanGroupChannel | null): Promise<PlanBucket[]> {
  const params = new URLSearchParams();
  if (carrier) params.set("carrier", carrier);
  if (channel) params.set("channel", channel);
  return fetchJson(`${API}/plan-buckets${params.size ? `?${params}` : ""}`);
}

/**
 * 표기 지정 창이 쓰는 목록 — 그 업체의 통신사 × 도매/온라인 한 벌만 (티켓 #839).
 *
 * 도매인지 온라인인지는 화면이 판단하지 않고 업체 이름만 넘긴다. 시세표 계산과 같은 규칙
 * (`policyVendorChannel`)으로 서버가 풀어야 「지정한 대로 값이 붙는」 축이 하나로 유지된다.
 */
export function fetchVendorPlanBuckets(vendorName: string, carrier?: Carrier | null): Promise<PlanBucket[]> {
  const params = new URLSearchParams();
  if (carrier) params.set("carrier", carrier);
  params.set("vendor", vendorName);
  return fetchJson(`${API}/plan-buckets?${params}`);
}

/**
 * 리베이트 합산 표가 도매·온라인 두 벌을 한 표에 겹쳐 그리는 재료 (티켓 #857).
 *
 * 표는 구간 «코드»로 줄을 세우는데 구간표가 두 벌로 갈린 뒤로는 같은 `P_119` 도 코드가 달라
 * (`SK_W_…` / `SK_O_…`) 다른 벌 업체의 값이 앉을 줄을 못 찾고 통째로 빠졌다. 겹쳐 그리려면
 * 두 벌 카탈로그 · 업체의 도매/온라인 · 이름이 같은 두 구간의 «속이 다른 정도»가 필요한데,
 * 왕복을 줄이려고 한 창구에서 함께 받는다. 도매/온라인 전환은 이 응답을 다시 받지 않고
 * 이미 받아 둔 완성본을 다시 그리기만 한다.
 */
export type PlanBucketScopeMap = {
  buckets: PlanBucket[];
  /** 업체 이름 → 도매/온라인. 판정은 화면이 아니라 시세표와 같은 함수로 서버가 푼다. */
  vendorChannels: Record<string, PlanGroupChannel>;
  /** 이름이 같은 도매·온라인 구간 한 쌍 — differentPlans 는 한쪽에만 있는 요금제 수. */
  pairs: {
    carrier: Carrier;
    labelKey: string;
    wholesaleCode: string;
    onlineCode: string;
    differentPlans: number;
  }[];
};

export function fetchPlanBucketScopeMap(): Promise<PlanBucketScopeMap> {
  return fetchJson(`${API}/plan-buckets/scope-map`);
}

/** 표기 지정 창의 목록 캐시 열쇠 — 창을 열 때 한 번만 받고 5분간 그대로 쓴다. */
export function vendorPlanBucketsQk(vendorName: string, carrier?: Carrier | null) {
  return ["policy", "plan-buckets", "vendor", vendorName, carrier ?? "all"] as const;
}

/** 요금제 조건 표기("SK_50 이상"·"전체"·"SK_90")를 실제로 커버하는 표준 구간 코드들로 펼친다(높은 구간부터).
 *  "이상/전체" 같은 애매한 표기 대신 그게 가리키는 요금제군을 그대로 노출하기 위한 표시 전용 확장 —
 *  저장 데이터(planRange)는 건드리지 않는다. 카탈로그에 없는 비표준 표기는 원문 하나 그대로 둔다. */
export function expandPlanRange(planRange: string, buckets: PlanBucket[]): string[] {
  if (!planRange) return [planRange];
  const sorted = [...buckets].sort((a, b) =>
    a.channel && b.channel ? (a.sortOrder ?? 0) - (b.sortOrder ?? 0) : b.minMonthly - a.minMonthly,
  );
  if (planRange === "전체") return sorted.length ? sorted.map((b) => b.code) : [planRange];
  const above = planRange.endsWith(" 이상");
  const code = above ? planRange.replace(/ 이상$/, "") : planRange;
  const base = sorted.find((b) => b.code === code);
  if (!base) return [planRange]; // 비표준·미매핑 표기는 원문 유지
  if (!above) return [code];
  // "X 이상"은 같은 통신사 안에서만 위로 펼친다 — 코드 접두어(SK_/KT_/LG_)가 통신사. 전 통신사 카탈로그를
  // 받은 호출부(견적서 툴팁)에서도 KT 조건이 SK·LG 구간까지 번지지 않게.
  return sorted
    .filter((b) => b.carrier === base.carrier && (base.channel
      ? b.channel === base.channel && (b.sortOrder ?? 0) <= (base.sortOrder ?? 0)
      : !b.channel && b.minMonthly >= base.minMonthly))
    .map((b) => b.code);
}

/** 표준 구간 코드("SK_50")를 표시 라벨로 — 카탈로그 label 에서 "이상" 등 연산자 낱말을 떼고 요금제군만
 *  남긴다("110K 이상"→"110K"). 카탈로그에 없으면 코드 문자열에서 연산자 낱말만 정리해 그대로. */
export function bucketLabelOf(code: string, buckets: PlanBucket[]): string {
  const b = buckets.find((x) => x.code === code);
  return stripBandWords(b?.label ?? code);
}

/** 요금제 조건 표기("SK_50 이상"·"전체")를 한 줄 라벨로 — "이상/전체" 대신 커버 요금제군들을 나열("50K·61K·90K").
 *  카탈로그에 없는 비표준 표기는 접미사만 떼고 원문 유지. 텍스트 요약(툴팁·상세표 한 칸)용. */
export function planRangeLabel(planRange: string, buckets: PlanBucket[]): string {
  // 확장 결과를 라벨로 — 매칭 코드는 카탈로그 라벨("110K"), 미매칭 원문은 연산자 낱말만 정리(bucketLabelOf 안).
  return expandPlanRange(planRange, buckets)
    .map((c) => bucketLabelOf(c, buckets))
    .join("·");
}

/** 표준 구간 추가 — code 는 서버가 carrier+minMonthly 로 생성한다(합산 정규식 호환 보장) */
export function createPlanBucket(body: {
  carrier: Carrier;
  channel?: PlanGroupChannel;
  label: string;
  minMonthly: number;
  maxMonthly: number | null;
  sortOrder?: number;
  description?: string;
}): Promise<PlanBucket> {
  return fetchJson(`${API}/plan-buckets`, { method: "POST", body: JSON.stringify(body) });
}

/** 구간 이름 여러 개를 한 번에 추가 — 「📋 붙여넣어 한 번에 만들기」의 [이대로 만들기] 한 번이 이 창구 하나로 나간다.
 *  구간을 하나 만들 때마다 전 매장 시세표 계산 결과가 다시 만들어지므로 반복 호출하지 않는다(티켓 #825). */
export function createPlanBucketsBulk(body: {
  carrier: Carrier;
  channel: PlanGroupChannel;
  labels: string[];
}): Promise<{ ok: true; created: PlanBucket[]; skipped: number }> {
  return fetchJson(`${API}/plan-buckets/bulk`, { method: "POST", body: JSON.stringify(body) });
}

/** 표준 구간 수정 — 표시 이름·상한·정렬만(carrier·하한=code 정체성은 불변) */
export function updatePlanBucket(
  code: string,
  body: { label?: string; minMonthly?: number; maxMonthly?: number | null; sortOrder?: number; description?: string },
): Promise<PlanBucket> {
  return fetchJson(`${API}/plan-buckets/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** 표준 구간 삭제 — 이 코드를 쓰던 정책 행은 합산에서 빠진다 */
export function deletePlanBucket(code: string): Promise<{ ok: true }> {
  return fetchJson(`${API}/plan-buckets/${encodeURIComponent(code)}`, { method: "DELETE" });
}

export function reorderPlanBuckets(body: { carrier: Carrier; channel: PlanGroupChannel; codes: string[] }): Promise<{ ok: true }> {
  return fetchJson(`${API}/plan-buckets/reorder`, { method: "POST", body: JSON.stringify(body) });
}

export function activatePlanBuckets(body: { carrier: Carrier; channel: PlanGroupChannel }): Promise<{ ok: true; active: true }> {
  return fetchJson(`${API}/plan-buckets/activate`, { method: "POST", body: JSON.stringify(body) });
}

/** 시세표 구간 적용 해제 — 스위치만 내린다. 만들어 둔 구간·요금제 배정은 그대로 남는다. */
export function deactivatePlanBuckets(body: { carrier: Carrier; channel: PlanGroupChannel }): Promise<{ ok: true; active: false }> {
  return fetchJson(`${API}/plan-buckets/deactivate`, { method: "POST", body: JSON.stringify(body) });
}

/** 지금 적용하면 시세표에서 빠질 것들 — 확인창을 **열 때 한 번만** 부른다(무거운 집계). */
export interface PlanBucketActivationPreview {
  /** 구간 이름과 맞지 않는 도매업체 표기 — 업체별 건수와 예시 표기 */
  vendors: { vendorName: string; count: number; samples: string[] }[];
  /** 맞지 않아 시세표에서 빠질 액면 행 수 */
  unmatchedRows: number;
  /** 맞지 않는 표기의 가짓수(업체별 중복 제외) */
  unmatchedLabels: number;
  /** 적용을 누르면 시세표가 다시 계산되는 매장 수 — 이 버튼이 얼마나 무거운 일인지 사람에게 알린다 */
  affectedStores: number;
  /**
   * 아직 어느 구간에도 안 붙은 요금제 수 — 이것도 적용 뒤 값이 안 나온다.
   *
   * 종전엔 「요금제군 한눈에」 화면이 자기 표에서 세어 확인창에 넘겼다. 적용 버튼이 전체 리베이트
   * 표로 옮겨 가면서(티켓 #841) 세는 자리를 서버로 옮겼다 — 화면이 세게 두면 그 줄이 조용히 0 이 된다.
   */
  unassignedPlans: number;
}

/**
 * 「새 구간으로 미리보기」 재료 — 그 통신사 × 판매유형의 구간 목록·순서와 지금 적용 중인지(티켓 #841).
 *
 * **계산이 아니라 조회다.** 어느 요금제 표기가 어느 구간에 앉는지는 화면이 이미 받아 둔 전체
 * 리베이트 표 위에서 서버와 같은 순수 규칙(shared/plan-bucket-scope)으로 직접 판정한다.
 */
export interface PlanBucketPreview {
  carrier: Carrier;
  channel: PlanGroupChannel;
  /** 이 칸이 지금 시세표에 적용 중인가 — 버튼이 [반영]인지 [반영 해제]인지를 가른다 */
  active: boolean;
  /** 위→아래 순서가 곧 표의 왼→오른쪽 열 순서다 */
  buckets: { code: string; label: string; description: string }[];
}

export function fetchPlanBucketPreview(carrier: Carrier, channel: PlanGroupChannel): Promise<PlanBucketPreview> {
  return fetchJson(`${API}/plan-buckets/preview?carrier=${carrier}&channel=${channel}`);
}

/** 지금 이 매장 시세표를 다시 굽고 있나 — 굽는 중에는 확정 버튼을 잠가 요청이 쌓이지 않게 한다. */
export function fetchPlanBucketWarmStatus(): Promise<{ warming: boolean }> {
  return fetchJson(`${API}/plan-buckets/warm-status`);
}

export function fetchPlanBucketActivationPreview(carrier: Carrier, channel: PlanGroupChannel): Promise<PlanBucketActivationPreview> {
  return fetchJson(`${API}/plan-buckets/activation-preview?carrier=${carrier}&channel=${channel}`);
}

/** 여섯 칸(통신사 3 × 판매유형 2)을 한 번에 세어 온다(티켓 #858) — 「요금제군 한눈에」 제목 옆
 *  큰 버튼의 확인창이 칸마다 한 줄씩 보여 주는 재료다. 칸마다 따로 부르면 창을 여는 데만
 *  몇 초가 걸리므로 **한 요청**으로 받는다. */
export interface PlanBucketCellPreview {
  carrier: Carrier;
  channel: PlanGroupChannel;
  /** 이미 시세표에 반영 중인 칸 — 이번 반영에서 그대로 둔다 */
  active: boolean;
  /** 만들어 둔 구간 수. 0 이면 반영을 건너뛴다(전체가 막히지 않게) */
  bucketCount: number;
  unmatchedRows: number;
  unmatchedLabels: number;
  unassignedPlans: number;
  vendors: { vendorName: string; count: number; samples: string[] }[];
}

export interface PlanBucketActivationPreviewAll {
  /** 반영을 누르면 시세표가 다시 계산되는 매장 수 */
  affectedStores: number;
  cells: PlanBucketCellPreview[];
}

/** 여섯 칸의 «반영 중인가·구간을 만들었나»만 받아 온다 — 값싼 조회라 화면을 열 때 한 번 받는다.
 *  버튼 문구(「아직 반영 안 된 N칸」·「✓ 여섯 칸 모두 반영 중」)가 이 값으로 정해진다. */
export interface PlanBucketCellState {
  carrier: Carrier;
  channel: PlanGroupChannel;
  active: boolean;
  bucketCount: number;
}

export function fetchPlanBucketCellStates(): Promise<{ cells: PlanBucketCellState[] }> {
  // ⚠️ **절대 캐시에서 읽지 않는다.** 이 값은 「아직 반영 안 된 N칸」이라는 **버튼 문구**가 되므로,
  //    옛 값을 들고 있으면 버튼이 거짓말을 한다(반영이 남았는데 「✓ 모두 반영 중」으로 잠기거나
  //    그 반대). 다른 화면(전체 리베이트 표)에서 반영·해제할 수 있어 더 그렇다.
  return fetchJson(`${API}/plan-buckets/cell-states`, { cache: "no-store" });
}

export function fetchPlanBucketActivationPreviewAll(): Promise<PlanBucketActivationPreviewAll> {
  return fetchJson(`${API}/plan-buckets/activation-preview-all`);
}

/** 여섯 칸을 한 번에 시세표에 반영한다(티켓 #858).
 *  이미 반영 중인 칸과 구간이 0개인 칸은 서버가 건너뛰고, **전 매장 캐시 무효화는 요청당 한 번**만
 *  일어난다 — 칸마다 따로 부르면 가격표가 여섯 번 다시 구워진다. */
export function activateAllPlanBuckets(): Promise<{
  ok: true;
  applied: { carrier: Carrier; channel: PlanGroupChannel }[];
  skipped: { carrier: Carrier; channel: PlanGroupChannel; reason: "active" | "no-buckets" }[];
}> {
  return fetchJson(`${API}/plan-buckets/activate-all`, { method: "POST" });
}

export function assignPlanGroupMatrixRows(body: {
  carrier: Carrier;
  channel: PlanGroupChannel;
  bucketCode: string;
  plans: { planId: string | null; planName: string; audience: string }[];
}): Promise<{ ok: true; updated: number }> {
  return fetchJson(`${API}/plan-groups/matrix/assign`, { method: "POST", body: JSON.stringify(body) });
}

/** 여러 구간 몫을 한 번에 — 붙여넣기 대조 창의 [이대로 저장] 한 번이 이 창구 하나로 나간다. */
export function assignPlanGroupMatrixRowsBatch(body: {
  carrier: Carrier;
  channel: PlanGroupChannel;
  groups: { bucketCode: string; plans: { planId: string | null; planName: string; audience: string }[] }[];
}): Promise<{ ok: true; updated: number }> {
  return fetchJson(`${API}/plan-groups/matrix/assign-batch`, { method: "POST", body: JSON.stringify(body) });
}

/** 「가입제한 요금제」 목록을 그 통신사 것으로 **통째로** 간다(전에 넣은 건 자동 정리, 티켓 #830).
 *  통신사 축 하나로만 저장되므로 도매·온라인 양쪽에 다 걸린다. 시세표는 다시 만들어지지 않는다. */
export function replacePlanRegistrationRestrictions(body: {
  carrier: Carrier;
  plans: { planId: string | null; planName: string }[];
}): Promise<{ ok: true; count: number }> {
  return fetchJson(`${API}/plan-groups/matrix/restrictions`, { method: "PUT", body: JSON.stringify(body) });
}

/** 목록에서 한 줄의 「가입제한 요금제」 표만 붙이거나 뗀다. */
export function togglePlanRegistrationRestriction(body: {
  carrier: Carrier;
  planId: string | null;
  planName: string;
  restricted: boolean;
}): Promise<{ ok: true; restricted: boolean }> {
  return fetchJson(`${API}/plan-groups/matrix/restrictions/toggle`, { method: "POST", body: JSON.stringify(body) });
}

/** 고른 요금제 전부를 「가입제한 요금제」로 지정한다(티켓 #858) — **한 번의 요청**으로 모아 보낸다.
 *  줄마다 toggle 을 부르면 한 번에 수십 요청이 나간다(원본 구간표에서 칸을 골라 지정하는 자리는
 *  수십 건이 정상 규모다). 표시만 바꾸는 저장이라 시세표는 다시 만들어지지 않는다.
 *  위 PUT(통째로 갈기)과 달리 **기존 목록을 지우지 않고 얹는다.** */
export function markPlansRestricted(body: {
  carrier: Carrier;
  plans: { planId: string | null; planName: string }[];
}): Promise<{ ok: true; count: number }> {
  return fetchJson(`${API}/plan-groups/matrix/restrictions/bulk`, { method: "POST", body: JSON.stringify(body) });
}

/** 업체별 모델 그룹 1건 — 구두 검토 그리드 왼쪽 "구분" 열에서 결과 행을 묶는 표시 전용 묶음.
 *  더 이상 LLM 분석에는 쓰지 않는다(분석 범위는 액면에서 도출). 업체에 귀속된다(2026-06-13). */
export type ModelGroup = {
  id: number;
  vendorId: number; // 귀속 업체
  name: string;
  modelCodes: string[]; // models.code 배열 — 표시 이름은 fetchModels 카탈로그로 만든다
  /** 공통그룹을 제외하고 직접/AI로 고른 모델. 연결 그룹 편집 화면의 원본 상태 복원용. */
  directModelCodes: string[];
  /** 정책 자동화에서 연결한 공통그룹. active가 아니면 마지막 정상 모델을 fallback으로 보낸다. */
  globalGroups: {
    globalGroupId: number;
    name: string;
    modelCodes: string[];
    allModelCodes: string[];
    excludedModelCodes: string[];
    totalModelCount: number;
    status: "active" | "deleted" | "empty";
  }[];
  /** 같은 업체에서 매장이 만든 다른 구분그룹 연결. */
  policyGroups: {
    groupId: number;
    name: string;
    modelCodes: string[];
    allModelCodes: string[];
    excludedModelCodes: string[];
    totalModelCount: number;
    status: "active" | "deleted" | "empty";
  }[];
  createdAt: string; // ISO
  /** 구두 그룹 알림용: 최근 7장 필터 전 실제 그룹 정의와 최근 7일 업체 이력. */
  definitionModelCodes?: string[];
  recentVendorModelCodes?: string[];
  recentVendorUploadCount?: number;
};

export type ModelGroupGlobalRefInput = {
  globalGroupId: number;
  snapshotName: string;
  snapshotModelCodes: string[];
  excludedModelCodes?: string[];
};

export type ModelGroupPolicyRefInput = {
  groupId: number;
  snapshotName: string;
  snapshotModelCodes: string[];
  excludedModelCodes?: string[];
};

export type ModelGroupChangePreview = {
  affectedEntries: Array<{
    id: number;
    name: string | null;
    periodFrom: string | null;
    periodTo: string | null;
    addedModelCount: number;
    removedModelCount: number;
    unresolvedModelCount: number;
    /** 모델 이름(없으면 코드). 옛 서버 응답엔 없을 수 있다. */
    addedModelNames?: string[];
    removedModelNames?: string[];
    unresolvedModelNames?: string[];
  }>;
};

export type ModelGroupUpdateInput = {
  name: string;
  modelCodes: string[];
  vendorId?: number;
  globalGroupRefs?: ModelGroupGlobalRefInput[];
  policyGroupRefs?: ModelGroupPolicyRefInput[];
};

/** 모델 그룹 목록 (이름 순). vendorId 를 주면 그 업체 그룹만, 없으면 매장 전체.
 *  light 를 주면 서버가 참조 펼침을 건너뛰고 저장값을 그대로 내려준다 — 이름·기종 수·공통 연결
 *  여부만 쓰는 목록 화면용. 편집처럼 정확한 최신 상태가 필요하면 light 없이(업체 지정해) 부른다. */
export function fetchModelGroups(vendorId?: number, opts?: { light?: boolean }): Promise<ModelGroup[]> {
  const params = new URLSearchParams();
  if (vendorId != null) params.set("vendorId", String(vendorId));
  if (opts?.light) params.set("light", "1");
  const qs = params.size > 0 ? `?${params}` : "";
  return fetchJson(`${API}/model-groups${qs}`);
}

/** 모델 그룹 생성 — 업체 + 이름 + 모델 1개 이상. 같은 업체 안 이름 중복은 409 */
export function createModelGroup(input: {
  vendorId: number;
  name: string;
  modelCodes: string[];
  globalGroupRefs?: ModelGroupGlobalRefInput[];
  policyGroupRefs?: ModelGroupPolicyRefInput[];
}): Promise<{ id: number }> {
  return fetchJson(`${API}/model-groups`, { method: "POST", body: JSON.stringify(input) });
}

/** 모델 그룹 수정 — 이름·기종 교체(업체 재할당은 vendorId). 같은 업체 안 이름 중복은 409 */
export function updateModelGroup(
  id: number,
  input: ModelGroupUpdateInput,
): Promise<{ ok: true } & ModelGroupChangePreview> {
  return fetchJson(`${API}/model-groups/${id}`, { method: "PATCH", body: JSON.stringify(input) });
}

/** [수정 저장] 전 확인창에 어느 종료 전 구두가 따라 바뀌는지 계산만 한다. */
export function previewModelGroupChange(id: number, input: ModelGroupUpdateInput): Promise<ModelGroupChangePreview> {
  return fetchJson(`${API}/model-groups/${id}/change-preview`, { method: "POST", body: JSON.stringify(input) });
}

/** 모델 그룹 삭제 */
export function deleteModelGroup(id: number): Promise<{ ok: true }> {
  return fetchJson(`${API}/model-groups/${id}`, { method: "DELETE" });
}

// ───────────────────── 시세표 모델 그룹 (시세표 표·엑셀 구분 묶음 · 매장 공용) ─────────────────────

/** 시세표용 모델 그룹 1건 — 시세표에서 모델을 "플래그십·보급형" 같은 묶음으로 나눠 표·엑셀에 소제목으로
 *  구분하는 매장 공용 묶음. 구두 그룹과 달리 업체 무관이고 그룹 자체 표시 순서(sortOrder)를 가진다. */
export type QuoteModelGroup = {
  id: number;
  name: string;
  modelCodes: string[]; // models.code 배열 — 표시 이름은 fetchModels 카탈로그로 만든다
  sortOrder: number;
  createdAt: string; // ISO
  /** true 면 운영사 공통 그룹 — 매장에서 내용 수정·삭제는 불가하지만 표시 순서는 바꿀 수 있다. */
  isGlobal?: boolean;
};

/** 시세표 모델 그룹 생성 — 이름 + 모델 1개 이상. 매장 안 이름 중복은 409. 끝 순서로 추가됨. */
export function createQuoteModelGroup(input: {
  name: string;
  modelCodes: string[];
}): Promise<{ id: number }> {
  return fetchJson(`${API}/quote-sheet/model-groups`, { method: "POST", body: JSON.stringify(input) });
}

/** 시세표 모델 그룹 수정 — 이름·기종 교체. 매장 안 이름 중복은 409. */
export function updateQuoteModelGroup(
  id: number,
  input: { name: string; modelCodes: string[] },
): Promise<{ ok: true }> {
  return fetchJson(`${API}/quote-sheet/model-groups/${id}`, { method: "PATCH", body: JSON.stringify(input) });
}

/** 시세표 모델 그룹 표시 순서 일괄 변경 — 매장·공통 그룹 id를 섞은 순서를 매장별로 저장. */
export function reorderQuoteModelGroups(ids: number[]): Promise<{ ok: true }> {
  return fetchJson(`${API}/quote-sheet/model-groups/reorder`, { method: "POST", body: JSON.stringify({ ids }) });
}

/** 시세표 모델 그룹 삭제 */
export function deleteQuoteModelGroup(id: number): Promise<{ ok: true }> {
  return fetchJson(`${API}/quote-sheet/model-groups/${id}`, { method: "DELETE" });
}

// ───────────────────── 공통 모델 그룹 (운영사 전역 · 티켓 #41) ─────────────────────

/** 공통 모델 그룹 1건 — 운영사(본사)가 한 번만 만들어 전 매장·전 업체가 그대로 쓰는 전역 묶음.
 *  조회는 전 계정, 쓰기는 본사 관리자만(서버가 403 으로 강제). */
export type GlobalModelGroup = {
  id: number;
  name: string;
  modelCodes: string[]; // models.code 배열 — 표시 이름은 fetchModels 카탈로그로 만든다
  sortOrder: number;
  createdAt: string; // ISO
  /** 그룹 원본 코드 전부 — includeUnavailable 로 부를 때만 온다(티켓 #477, "4/5" 의 분모) */
  allModelCodes?: string[];
  /** 그 업체 정책표에서 확인되지 않아 modelCodes 에서 빠진 코드 (같은 조건에서만 온다) */
  missingModelCodes?: string[];
  /** 구두 그룹 알림용이며 includeUnavailable 응답에만 온다. */
  definitionModelCodes?: string[];
  recentVendorModelCodes?: string[];
  recentVendorUploadCount?: number;
};

/** 공통 그룹 목록 (순서 → 이름). vendorId 를 주면 그 업체가 취급하는 모델만 남긴 사본이 온다
 *  (빈 그룹은 빠짐 — 구두 관리 칩용). 없으면 전체 원본(본사 관리 모달용).
 *
 *  `includeUnavailable` 을 주면(티켓 #477) 빠진 사실까지 함께 온다 — 하나도 안 걸린 그룹도
 *  `modelCodes: []` 로 남고, `allModelCodes`·`missingModelCodes` 가 채워진다. 구두 관리 칩이
 *  "4/5 · 정책표에 없음"을 말하기 위한 재료라, 그 화면 말고는 이 옵션을 쓰지 않는다. */
export function fetchGlobalModelGroups(
  vendorId?: number,
  opts?: { includeUnavailable?: boolean },
): Promise<GlobalModelGroup[]> {
  const params = new URLSearchParams();
  if (vendorId != null) params.set("vendorId", String(vendorId));
  if (opts?.includeUnavailable) params.set("includeUnavailable", "1");
  const q = params.toString();
  return fetchJson(`${API}/global-model-groups${q ? `?${q}` : ""}`);
}

/** 공통 그룹 생성 (본사 관리자만) — 이름 유일 + 모델 1개 이상. 끝 순서로 추가됨. */
export function createGlobalModelGroup(input: {
  name: string;
  modelCodes: string[];
}): Promise<{ id: number }> {
  return fetchJson(`${API}/global-model-groups`, { method: "POST", body: JSON.stringify(input) });
}

/** 공통 그룹 수정 (본사 관리자만) */
export function updateGlobalModelGroup(
  id: number,
  input: { name: string; modelCodes: string[] },
): Promise<{ ok: true } & ReprocessReceipt> {
  return fetchJson<{ ok: true } & ReprocessReceipt>(`${API}/global-model-groups/${id}`, { method: "PATCH", body: JSON.stringify(input) }).then(announceReprocessing);
}

/** 공통 그룹 표시 순서 일괄 변경 (본사 관리자만) — ids 순서대로 sortOrder 0..n. */
export function reorderGlobalModelGroups(ids: number[]): Promise<{ ok: true }> {
  return fetchJson(`${API}/global-model-groups/reorder`, { method: "POST", body: JSON.stringify({ ids }) });
}

/** 공통 그룹 삭제 (본사 관리자만) */
export function deleteGlobalModelGroup(id: number): Promise<{ ok: true } & ReprocessReceipt> {
  return fetchJson<{ ok: true } & ReprocessReceipt>(`${API}/global-model-groups/${id}`, { method: "DELETE" }).then(announceReprocessing);
}

/** 구간표 카탈로그의 요금제 1건 — 구간(bucket) 또는 제외/구간없음 상태를 가진다 */
export type PlanGroup = {
  id: string;
  channel: PlanGroupChannel;
  planId: string | null;
  planName: string;
  monthly: number | null; // 월정액(원)
  groupRaw: string; // 원문 군 표기 ("P_109", "110K 이상", "115군", "기본구간")
  bucketCode: string | null;
  bucketLabel: string | null;
  excluded: boolean; // true = 리베 대상 아님 (기본구간·0그룹)
  source: string; // 'sk-lex' | 'sk-online-csv' | 'sk-ps' | 'kt-notice' | 'lg-notice'
  effectiveFrom: string | null; // 구간표 유효 시작일
};

export type PlanGroupChannel = "wholesale" | "online";

/** 요금제 검색 — 구간표 카탈로그에서 이름 부분일치, 월정액 높은 순 30건. all=true 면 전체 */
export function fetchPlanGroups(
  carrier: Carrier,
  q: string,
  all?: boolean,
  channel: PlanGroupChannel = "wholesale",
): Promise<PlanGroup[]> {
  const qs = new URLSearchParams({ carrier, channel, ...(q ? { q } : {}), ...(all ? { all: "1" } : {}) });
  return fetchJson(`${API}/plan-groups?${qs}`);
}

export type PlanGroupUploadVendor = {
  id: number;
  name: string;
  displayName: string;
  carrier: Carrier;
  channel: PlanGroupChannel;
};

type PlanGroupUploadSheetInfo = {
  sheetNames: string[];
  selectedSheetName: string | null;
  fileHash: string;
};

export type PlanGroupUploadPreview = PlanGroupUploadSheetInfo & ({
  status: "ready";
  channel: PlanGroupChannel;
  carrier: Carrier;
  format: string;
  rowCount: number;
  currentCount: number;
  effectiveFrom: string | null;
  unmatchedPlans: string[];
  unmatchedGroups: string[];
  warnings: string[];
  suggestedVendorId: number | null;
  manualConfig: PlanGroupGridConfig | null;
  groupMappings: PlanGroupGridMappings;
} | {
  status: "not-found";
  error: string;
} | {
  status: "configure";
  error: string;
  channel: PlanGroupChannel;
  carrier: Carrier;
  grid: FaceUploadGrid;
  manualConfig: PlanGroupGridConfig | null;
  groupMappings: PlanGroupGridMappings;
  trial: PlanGroupGridTrial | null;
});

/** 구간표 반영 권한자가 고를 수 있는 기존 정책 대리점 목록. */
export function fetchPlanGroupUploadVendors(): Promise<PlanGroupUploadVendor[]> {
  return fetchJson<unknown>(`${API}/plan-groups/upload-vendors`).then(
    withContract(z.array(z.looseObject({ id: z.number() })), "plan-groups/upload-vendors"),
  ) as Promise<PlanGroupUploadVendor[]>;
}

/**
 * 업로드 미리보기 응답의 **뼈대**만 검사한다(src/api/contract.ts 규칙) — 화면이 세거나 펼치는
 * 목록(unmatchedPlans·sheetNames)이 응답 종류(status)별로 반드시 있는지.
 * 2026-09-03 사고: 서버가 새 종류 "not-found"(탭을 고르세요)를 내기 시작했는데 배포 전부터
 * 열려 있던 옛 화면이 그 응답을 "ready"로 알고 unmatchedPlans.length 를 세다 터졌다.
 * 이제 어긋나면 크래시 대신 "새로고침해 주세요" 문구로 끝난다.
 */
const planGroupUploadPreviewContract = z.discriminatedUnion("status", [
  z.looseObject({
    status: z.literal("ready"),
    unmatchedPlans: z.array(z.string()),
    unmatchedGroups: z.array(z.string()),
    sheetNames: z.array(z.string()),
  }),
  z.looseObject({
    status: z.literal("not-found"),
    sheetNames: z.array(z.string()),
  }),
  z.looseObject({
    status: z.literal("configure"),
    sheetNames: z.array(z.string()),
    grid: z.looseObject({ uploadId: z.number() }),
    groupMappings: z.record(z.string(), z.string()),
  }),
]);

/** 파일 한 장을 내용으로 판별해 읽기만 한다. DB는 바꾸지 않는다. */
export function previewPlanGroupUpload(
  file: File,
  channel: PlanGroupChannel,
  vendorId?: number | null,
  sheetName?: string,
): Promise<PlanGroupUploadPreview> {
  const form = new FormData();
  form.append("file", file);
  form.append("channel", channel);
  if (vendorId != null) form.append("vendorId", String(vendorId));
  if (sheetName) form.append("sheetName", sheetName);
  return fetchJsonForm<unknown>(`${API}/plan-groups/upload-preview`, form).then(
    withContract(planGroupUploadPreviewContract, "plan-groups/upload-preview"),
  ) as Promise<PlanGroupUploadPreview>;
}

/**
 * 아직 서버에 올리지 않은 구간표 한 장을 «원본 엑셀 모양» 격자로만 받아 온다(티켓 #835).
 * 읽기·판정은 하지 않고 DB 도 건드리지 않는다 — 여러 장 올리기 창의 대조용 보기 전용 통로다.
 */
export function previewPlanGroupUploadGrid(file: File, sheetName?: string): Promise<FaceUploadGrid> {
  const form = new FormData();
  form.append("file", file);
  if (sheetName) form.append("sheetName", sheetName);
  return fetchJsonForm<FaceUploadGrid>(`${API}/plan-groups/upload-grid-view`, form);
}

function planGroupGridForm(input: {
  file: File;
  vendorId: number;
  channel: PlanGroupChannel;
  groupMappings: PlanGroupGridMappings;
}) {
  const form = new FormData();
  form.append("file", input.file);
  form.append("vendorId", String(input.vendorId));
  form.append("channel", input.channel);
  form.append("groupMappings", JSON.stringify(input.groupMappings));
  return form;
}

/** 사람이 찍은 칸으로 시험 파싱한다. DB와 원본 보관은 건드리지 않는다. */
export function trialPlanGroupUploadGrid(input: {
  file: File;
  vendorId: number;
  channel: PlanGroupChannel;
  config: PlanGroupGridConfig;
  groupMappings: PlanGroupGridMappings;
}): Promise<PlanGroupGridTrial> {
  const form = planGroupGridForm(input);
  form.append("manualConfig", JSON.stringify(input.config));
  return fetchJsonForm(`${API}/plan-groups/upload-grid-trial`, form);
}

/** 고른 탭을 기존 구독형 정책 분석 두뇌에 보여 주고 칸 초안 + 시험 결과를 받는다. */
export function suggestPlanGroupUploadGrid(input: {
  file: File;
  vendorId: number;
  channel: PlanGroupChannel;
  sheetName: string;
  groupMappings: PlanGroupGridMappings;
}): Promise<PlanGroupGridTrial & { reason: string }> {
  const form = planGroupGridForm(input);
  form.append("sheetName", input.sheetName);
  return fetchJsonForm(`${API}/plan-groups/upload-grid-suggest`, form, 6 * 60_000);
}

/** 미리 본 파일 지문과 같은 파일만 선택한 대리점 출처로 전역 구간표에 반영한다. */
export function applyPlanGroupUpload(input: {
  file: File;
  fileHash: string;
  vendorId: number;
  channel: PlanGroupChannel;
  sheetName: string | null;
  manualConfig?: PlanGroupGridConfig | null;
  groupMappings?: PlanGroupGridMappings;
}): Promise<{ ok: true; carrier: Carrier; rowCount: number; effectiveFrom: string | null; source: string }> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("fileHash", input.fileHash);
  form.append("vendorId", String(input.vendorId));
  form.append("channel", input.channel);
  if (input.sheetName) form.append("sheetName", input.sheetName);
  if (input.manualConfig) form.append("manualConfig", JSON.stringify(input.manualConfig));
  if (input.groupMappings) form.append("groupMappings", JSON.stringify(input.groupMappings));
  return fetchJsonForm(`${API}/plan-groups/upload-apply`, form);
}

/** 대리점별로 보관한 통신사 구간표 원본을 기존 원본 엑셀 뷰 형식으로 읽는다. */
export function fetchPlanGroupSourceGrid(input: {
  vendorId: number;
  carrier: Carrier;
  channel: PlanGroupChannel;
  sheetName?: string;
}): Promise<FaceUploadGrid> {
  const qs = new URLSearchParams({
    vendorId: String(input.vendorId),
    carrier: input.carrier,
    channel: input.channel,
  });
  if (input.sheetName) qs.set("sheetName", input.sheetName);
  return fetchJson(`${API}/plan-groups/source?${qs}`);
}

/** 대리점별 구간표 원본 파일을 원래 파일명으로 내려받는다. */
export async function downloadPlanGroupSourceOriginal(sourceId: number): Promise<void> {
  await downloadSigned(`${API}/plan-groups/sources/${sourceId}/original`);
}

// ───────────────────── 요금제군 확정 화면 (2026-09-05) ─────────────────────

/** 시스템이 이 대리점 구간표를 어떻게 읽었나 — 군 짝·요금제 전체·미대응·어긋남·메모. */
export function fetchPlanGroupReview(sourceId: number): Promise<PlanGroupReview> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/review`);
}

/** 원문 군 하나의 짝을 바꾼다. 그 군의 요금제(한 줄씩 고친 것 제외)가 모두 따라간다. */
export function patchPlanGroupReviewGroup(
  sourceId: number,
  body: { groupRaw: string; bucketCode: string | null; excluded: boolean },
): Promise<PlanGroupReview> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/groups`, { method: "PATCH", body: JSON.stringify(body) });
}

/** 요금제 한 줄만 따로 고친다. reset 이면 군 짝으로 되돌린다. */
export function patchPlanGroupReviewPlan(
  sourceId: number,
  rowId: string,
  body: { bucketCode: string | null; excluded: boolean } | { reset: true },
): Promise<PlanGroupReview> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/plans/${encodeURIComponent(rowId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function addPlanGroupReviewNote(
  sourceId: number,
  body: { target: PlanGroupReviewNote["target"]; text: string },
): Promise<PlanGroupReview> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/notes`, { method: "POST", body: JSON.stringify(body) });
}

export function deletePlanGroupReviewNote(sourceId: number, noteId: string): Promise<PlanGroupReview> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
}

/** 요금제군 한눈에 — 통신사 기준 구간 → 요금제 → 대리점 열 매트릭스. */
export function fetchPlanGroupMatrix(carrier: Carrier, channel: PlanGroupChannel): Promise<PlanGroupMatrix> {
  const qs = new URLSearchParams({ carrier, channel });
  return fetchJson(`${API}/plan-groups/matrix?${qs}`);
}

/** 매트릭스 칸 수정 — 그 대리점이 이 요금제를 치는 군 이름을 바꾼다(빈 칸이면 새로 만든다). */
export function setPlanGroupMatrixCell(input: {
  sourceId: number;
  rowId: string | null;
  plan: { planId: string | null; planName: string; monthly: number | null; audience: string };
  groupRaw: string;
  excluded: boolean;
}): Promise<unknown> {
  if (input.rowId) {
    return fetchJson(`${API}/plan-groups/sources/${input.sourceId}/plans/${encodeURIComponent(input.rowId)}`, {
      method: "PATCH",
      body: JSON.stringify({ groupRaw: input.groupRaw, excluded: input.excluded }),
    });
  }
  return fetchJson(`${API}/plan-groups/sources/${input.sourceId}/plans`, {
    method: "POST",
    body: JSON.stringify({ ...input.plan, groupRaw: input.groupRaw, excluded: input.excluded }),
  });
}

/** 매트릭스 여러 행 → 한 대리점 열을 한 번에. remove 면 사람이 만든 칸만 지운다. */
export function bulkSetPlanGroupMatrixCells(input: {
  sourceId: number;
  plans: { rowId: string | null; planId: string | null; planName: string; monthly: number | null; audience: string }[];
  groupRaw?: string;
  excluded?: boolean;
  remove?: boolean;
}): Promise<{ ok: true; updated: number; created: number; removed: number; skipped: number }> {
  return fetchJson(`${API}/plan-groups/sources/${input.sourceId}/plans/bulk`, {
    method: "POST",
    body: JSON.stringify({ plans: input.plans, groupRaw: input.groupRaw, excluded: input.excluded, remove: input.remove }),
  });
}

/** 매트릭스 칸 비우기 — 사람이 만든 칸만 지울 수 있다. */
export function clearPlanGroupMatrixCell(sourceId: number, rowId: string): Promise<unknown> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/plans/${encodeURIComponent(rowId)}`, {
    method: "PATCH",
    body: JSON.stringify({ remove: true }),
  });
}

/** 확정 — 이 대리점의 읽은 결과를 통신사×채널 계산표로 승격한다(시세표에 반영). */
export function confirmPlanGroupSource(sourceId: number): Promise<{ ok: true; promoted: number; review: PlanGroupReview }> {
  return fetchJson(`${API}/plan-groups/sources/${sourceId}/confirm`, { method: "POST" });
}

// ───────────── 대리점 구간표 여러 장 한 번에 올리기 (2026-09-05) ─────────────

/** 파일마다 대리점·통신사·채널을 정해 한 번에 제출한다. 서버는 줄에 세우고 곧바로 답한다 — 분석은 뒤에서 돈다. */
export function enqueuePlanGroupUploadJobs(
  items: Array<{ file: File; vendorId: number; carrier: Carrier; channel: PlanGroupChannel }>,
): Promise<{ batchId: string; jobs: PlanGroupUploadJob[] }> {
  const form = new FormData();
  for (const item of items) form.append("files", item.file);
  form.append(
    "items",
    JSON.stringify(items.map((item, index) => ({ index, vendorId: item.vendorId, carrier: item.carrier, channel: item.channel }))),
  );
  return fetchJsonForm(`${API}/plan-groups/upload-jobs`, form, REPROCESS_TIMEOUT_MS);
}

const planGroupUploadJobsContract = z.array(z.looseObject({ id: z.number(), status: z.string(), fileName: z.string() }));

/** 배치 하나(batchId) 또는 진행 중 전부(active). 둘 다 없으면 이 매장의 최근 것. */
export function fetchPlanGroupUploadJobs(input: { batchId?: string; activeOnly?: boolean } = {}): Promise<PlanGroupUploadJob[]> {
  const qs = new URLSearchParams();
  if (input.batchId) qs.set("batch", input.batchId);
  if (input.activeOnly) qs.set("active", "1");
  const suffix = qs.toString() ? `?${qs}` : "";
  return fetchJson<unknown>(`${API}/plan-groups/upload-jobs${suffix}`).then(
    withContract(planGroupUploadJobsContract, "plan-groups/upload-jobs"),
  ) as Promise<PlanGroupUploadJob[]>;
}

export function cancelPlanGroupUploadJob(id: number): Promise<{ ok: true; status: string }> {
  return fetchJson(`${API}/plan-groups/upload-jobs/${id}/cancel`, { method: "POST" });
}

export function retryPlanGroupUploadJob(id: number): Promise<{ ok: true }> {
  return fetchJson(`${API}/plan-groups/upload-jobs/${id}/retry`, { method: "POST" });
}

// ───────────────────── 「요금제군 한눈에」 엑셀 표 한 장 (티켓 #862) ─────────────────────

/** 표에서 고를 수 있는 요금제 한 줄 — 우리 카탈로그와 대리점 구간표를 이름으로 합친 것. */
export type PlanSheetPlan = {
  planId: string | null;
  planName: string;
  monthly: number | null;
  audience: string;
  /** 지금 「가입제한 요금제」로 지정돼 있나 */
  restricted: boolean;
  /** 마지막 저장 뒤에 크롤러가 새로 물어 온 요금제인가 */
  isNew: boolean;
};

export type PlanSheetCarrier = {
  carrier: Carrier;
  /** 이 통신사 표를 한 번이라도 저장한 때. null = 아직 한 번도 저장하지 않음(빈 표로 시작) */
  savedAt: string | null;
  bucketLabels: string[];
  rows: { planName: string; bucketLabel: string }[];
  plans: PlanSheetPlan[];
};

export type PlanSheetNewPlan = { carrier: Carrier; planId: string; planName: string; addedAt: string };

/** 세 통신사 표의 재료를 **한 요청**으로 받는다 — 화면을 열 때 한 번만 부른다.
 *  ⚠️ **절대 캐시에서 읽지 않는다.** 이 값은 「지금 저장돼 있는 표」라, 브라우저가 옛 응답을 한 번이라도
 *  되쓰면 **화면을 새로 열었는데 저장 전 표가 그대로 뜬다**(2026-09-07 검증에서 실제로 겪었다 —
 *  서버는 0줄이라는데 화면은 12줄이었다). 「반영 중인가」를 묻는 cell-states 와 같은 이유다. */
export function fetchPlanGroupSheet(): Promise<{ carriers: PlanSheetCarrier[]; newPlans: PlanSheetNewPlan[]; draft: PlanSheetDraft | null }> {
  return fetchJson(`${API}/plan-groups/sheet`, { cache: "no-store" });
}

/** [임시저장] — 표를 **그대로 담기만** 한다(티켓 #872).
 *  ⚠️ 시세표를 건드리는 창구(`POST /plan-groups/sheet`)와 **완전히 다른 자리**다. 여기는 구간 목록·
 *  배정·가입제한·전 매장 캐시를 하나도 손대지 않아 눌러도 기다림이 없다. 이 둘을 한 창구로 합치려는
 *  생각이 들면, 그 순간 임시저장의 의미(=적용하지 않고 담아 두기)가 없어진다는 것을 먼저 떠올릴 것. */
export function savePlanGroupSheetDraft(body: {
  carriers: PlanSheetDraftCarrier[];
}): Promise<{ ok: true; draft: PlanSheetDraft }> {
  return fetchJson(`${API}/plan-groups/sheet/draft`, { method: "PUT", body: JSON.stringify(body) });
}

/** [지금 적용된 표로 되돌리기] — 담아 둔 표를 버린다. 적용본은 손대지 않는다. */
export function discardPlanGroupSheetDraft(): Promise<{ ok: true }> {
  return fetchJson(`${API}/plan-groups/sheet/draft`, { method: "DELETE" });
}

/** 맨 위 알림 띠의 재료 — **화면을 열 때 한 번만**. 몇 초마다 자동 갱신하지 않는다(저녁에 요청이 10배로 뛴다). */
export function fetchNewlyAddedPlans(): Promise<{ plans: PlanSheetNewPlan[]; total: number }> {
  // ⚠️ 캐시에서 읽으면 **엑셀에 넣고 저장한 뒤에도 띠가 그대로 떠 있는다** — 그러면 이 띠는
  //    「없애는 법이 없는 경고」가 되어 아무도 안 읽게 된다(그걸 막으려고 운영사에게만 띄운 것이다).
  return fetchJson(`${API}/plan-groups/new-plans`, { cache: "no-store" });
}

/** [저장] 한 번 = 시스템 적용. 세 통신사 전체를 **한 요청**으로 보낸다 —
 *  칸마다·구간마다 부르면 그때마다 전 매장 시세표가 폐기·재생성된다. */
export function savePlanGroupSheet(body: {
  carriers: { carrier: Carrier; bucketLabels: string[]; rows: { planName: string; bucketLabel: string }[] }[];
}): Promise<{ ok: true; saved: { carrier: Carrier; buckets: number; rows: number }[] }> {
  return fetchJson(`${API}/plan-groups/sheet`, { method: "POST", body: JSON.stringify(body) });
}
